<template>
  <div class="salon-app salon-sheet-root" role="dialog" aria-modal="true" :aria-label="serviceName">
    <div class="salon-sheet-scrim" @click="$emit('close')"></div>

    <div class="salon-sheet">
      <span class="salon-sheet-handle" aria-hidden="true"></span>

      <div class="salon-sheet-progress">
        <div class="salon-progress-track">
          <div class="salon-progress-fill" :style="{ width: progressWidth }"></div>
        </div>
        <span class="salon-progress-label">{{ $t("salon.serviceDetail.stepOf", { step, total: totalSteps }) }}</span>
      </div>

      <div class="salon-sheet-body">
        <div class="salon-sheet-hero">
          <img
            v-if="heroImage && !heroBroken"
            :src="heroImage"
            :alt="serviceName"
            @error="heroBroken = true"
          />
          <button class="salon-sheet-close" type="button" :aria-label="$t('salon.serviceDetail.close')" @click="$emit('close')">
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" />
            </svg>
          </button>
        </div>

        <div class="salon-sheet-content">
          <h1 class="salon-sheet-title">{{ serviceName }}</h1>

          <div class="salon-sheet-facts">
            <span v-if="totalDurationMin" class="salon-sheet-duration">
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2" />
                <path d="M12 7v5l3 2" fill="none" stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              {{ $t("salon.serviceDetail.minutes", { n: totalDurationMin }) }}
            </span>
            <span v-else></span>
            <!-- SH032 — the outlet decides whether this sheet quotes a price. -->
            <span v-if="showPrice" class="salon-sheet-price">{{ formatPrice(totalPrice) }}</span>
          </div>

          <p v-if="description" class="salon-sheet-desc">{{ description }}</p>

          <!-- One block per modifier option group the menu set attached to this item.
               The reference only draws optional checkboxes, but a group can just as well
               be "pick exactly one" or mandatory, so those rules are honoured here rather
               than letting a customer submit a combination the menu set forbids. -->
          <section v-for="group in groups" :key="group.id" class="salon-addons">
            <div class="salon-addons-head">
              <h2 class="salon-addons-title">{{ group.name }}</h2>
              <span v-if="group.rule_label" class="salon-addons-rule">{{ group.rule_label }}</span>
            </div>

            <label
              v-for="option in group.items"
              :key="option.id"
              class="salon-addon"
              :class="{
                'salon-addon--on': isChecked(group, option),
                'salon-addon--off': !isAvailable(option),
              }"
            >
              <input
                class="salon-addon-input"
                :type="group.is_single ? 'radio' : 'checkbox'"
                :name="'salon_addon_' + group.id"
                :disabled="!isAvailable(option)"
                :checked="isChecked(group, option)"
                @change="toggle(group, option)"
              />
              <span class="salon-addon-box" :class="{ 'salon-addon-box--round': group.is_single }">
                <svg v-if="isChecked(group, option)" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                  <path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="3"
                    stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </span>

              <span class="salon-addon-labels">
                <span class="salon-addon-name">{{ option.item_name }}</span>
                <span v-if="option.service_duration_min" class="salon-addon-meta">
                  {{ $t("salon.serviceDetail.minutes", { n: option.service_duration_min }) }}
                </span>
              </span>

              <span v-if="showPrice && !group.is_remark && option.price > 0" class="salon-addon-price">
                +{{ formatPrice(option.price) }}
              </span>
            </label>
          </section>

          <section class="salon-notes">
            <h2 class="salon-addons-title">{{ $t("salon.serviceDetail.notesTitle") }}</h2>
            <textarea
              v-model="note"
              class="salon-notes-input"
              name="salon_service_note"
              rows="3"
              maxlength="500"
              :placeholder="$t('salon.serviceDetail.notesPlaceholder')"
              :aria-label="$t('salon.serviceDetail.notesTitle')"
            ></textarea>
          </section>
        </div>
      </div>

      <div class="salon-sheet-cta">
        <p v-if="unmetGroup" class="salon-sheet-hint">{{ $t("salon.serviceDetail.chooseOptionIn", { name: unmetGroup.name }) }}</p>
        <button class="salon-cta-btn" type="button" :disabled="!!unmetGroup || isLoading" @click="addToBooking">
          {{ $t("salon.serviceDetail.addToBooking") }}<template v-if="showPrice"> · {{ formatPrice(totalPrice) }}</template>
        </button>
      </div>

      <p v-if="loadFailed" class="salon-sheet-error">
        {{ $t("salon.serviceDetail.loadError") }}
        <button class="salon-retry" type="button" @click="getDetail">{{ $t("salon.serviceDetail.retry") }}</button>
      </p>
    </div>

    <material-loading :active="isLoading" />
  </div>
</template>

<script>
import MaterialLoading from "@/components/MaterialLoading.vue";
import OrderService from "@/services/order.service";
import "@/salon/styles/tokens.css";

// SH005 sits on top of SH004 — the sheet is opened from step 4 and the reference
// advances the progress bar to 5 while it is up (Salon_UI/service_detail_sheet).
const STEP = 5;
const TOTAL_STEPS = 9;

export default {
  name: "SalonServiceDetailSheet",
  components: {
    MaterialLoading,
  },
  props: {
    // The card that opened this sheet, so the header can render before the detail
    // call lands instead of flashing an empty sheet.
    service: {
      type: Object,
      required: true,
    },
    branchCode: {
      type: String,
      default: "",
    },
    menusetId: {
      type: [Number, String],
      default: 0,
    },
    // SH032 — minty_branches.salon_show_service_price, handed down by the service picker
    // that opened this sheet. False keeps the header price, the add-on prices and the
    // amount on the Add to booking button off the screen.
    showPrice: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close", "add"],
  data() {
    return {
      step: STEP,
      totalSteps: TOTAL_STEPS,
      isLoading: false,
      loadFailed: false,
      heroBroken: false,
      detail: null,
      // { [groupId]: number[] } — option ids chosen in each group.
      selection: {},
      note: this.service.note || "",
    };
  },
  computed: {
    serviceName() {
      return this.detail?.item_name || this.service.name || "";
    },

    description() {
      return this.detail?.description ?? this.service.description ?? "";
    },

    heroImage() {
      return this.detail?.image || this.service.image || "";
    },

    baseDurationMin() {
      return Number(this.detail?.service_duration_min ?? this.service.duration_min ?? 0);
    },

    // The price before any add-on. The salon flow signs the customer in at step 2 or 3,
    // so the member price is what they will pay. SH023 — the facts row prints the total
    // rather than this: half a row that moves with the checkboxes and half that does not
    // reads as a bug, and the base price is already the total until something is ticked.
    basePrice() {
      if (this.detail) {
        return Number(this.detail.member_price_no_modifier_option || 0);
      }

      return Number(this.service.price || 0);
    },

    groups() {
      const raw = this.detail?.modifier_group ?? [];

      return raw
        .filter((group) => (group.items ?? []).length)
        .map((group) => {
          const min = Number(group.min_qty || 0);
          const max = Number(group.max_qty || 0);

          return {
            ...group,
            name: group.name || this.$t("salon.serviceDetail.addOns"),
            is_single: max === 1,
            min_qty: min,
            max_qty: max,
            rule_label: this.ruleLabel(min, max),
          };
        });
    },

    // The first group whose minimum is not met yet. Doubles as the CTA's gate.
    unmetGroup() {
      return this.groups.find((group) => this.chosen(group.id).length < group.min_qty) || null;
    },

    totalPrice() {
      const addOns = this.groups.reduce((sum, group) => {
        return sum + this.chosenOptions(group).reduce((acc, option) => acc + Number(option.price || 0), 0);
      }, 0);

      return this.basePrice + addOns;
    },

    // SH023 — an add-on is a service of its own, so ticking one lengthens the
    // appointment as well as its bill. The chip has to move with the checkboxes or the
    // customer only meets the longer booking on the date step, where the minutes are
    // already being counted.
    totalDurationMin() {
      const addOns = this.groups.reduce((sum, group) => {
        return sum + this.chosenOptions(group).reduce(
          (acc, option) => acc + Number(option.service_duration_min || 0),
          0
        );
      }, 0);

      return this.baseDurationMin + addOns;
    },
  },
  created() {
    this.getDetail();
  },
  methods: {
    async getDetail() {
      this.isLoading = true;
      this.loadFailed = false;

      try {
        const response = await OrderService.getItemDetail({
          menuset_id: this.menusetId,
          category_id: this.service.category_id,
          item_id: this.service.id,
          branch_code: this.branchCode,
        });

        if (response.data.result.isSuccess) {
          this.detail = response.data.data;
          this.applyPreselection();
        } else {
          this.loadFailed = true;
        }
      } catch (error) {
        this.loadFailed = true;
      } finally {
        this.isLoading = false;
      }
    },

    // Two things preselect an option: the customer already configured this service and
    // came back to edit it, or the API marked an option `selected` because its group is
    // a mandatory single choice and it picked the first available one.
    applyPreselection() {
      const previous = this.service.selection || null;
      const selection = {};

      (this.detail.modifier_group ?? []).forEach((group) => {
        if (previous && Array.isArray(previous[group.id])) {
          selection[group.id] = [...previous[group.id]];
          return;
        }

        selection[group.id] = (group.items ?? [])
          .filter((option) => option.selected)
          .map((option) => option.id);
      });

      this.selection = selection;
    },

    ruleLabel(min, max) {
      if (min > 0 && max === 1) {
        return this.$t("salon.serviceDetail.required");
      }

      if (min > 0) {
        return this.$t("salon.serviceDetail.chooseAtLeast", { n: min });
      }

      if (max > 0 && max < 99) {
        return this.$t("salon.serviceDetail.upTo", { n: max });
      }

      return "";
    },

    chosen(groupId) {
      return this.selection[groupId] ?? [];
    },

    chosenOptions(group) {
      const ids = this.chosen(group.id);
      return (group.items ?? []).filter((option) => ids.includes(option.id));
    },

    isChecked(group, option) {
      return this.chosen(group.id).includes(option.id);
    },

    // Same stock rule the menu and the service list use.
    isAvailable(option) {
      if (option.stock_enable_balance) {
        return option.stock_balance > 0;
      }

      return option.is_not_available == 0;
    },

    toggle(group, option) {
      if (!this.isAvailable(option)) {
        return;
      }

      const current = [...this.chosen(group.id)];

      if (group.is_single) {
        // A required single-choice group cannot be emptied by tapping the chosen row —
        // that would leave the customer stuck behind the CTA gate with no way back.
        const isOnlyChoice = group.min_qty > 0 && current.length === 1 && current[0] === option.id;
        this.selection = {
          ...this.selection,
          [group.id]: isOnlyChoice ? current : [option.id],
        };
        return;
      }

      const index = current.indexOf(option.id);

      if (index !== -1) {
        current.splice(index, 1);
      } else {
        if (group.max_qty > 0 && current.length >= group.max_qty) {
          return;
        }
        current.push(option.id);
      }

      this.selection = { ...this.selection, [group.id]: current };
    },

    formatPrice(price) {
      return "$" + parseFloat(price || 0).toFixed(2);
    },

    addToBooking() {
      if (this.unmetGroup) {
        return;
      }

      const addOns = this.groups.flatMap((group) =>
        this.chosenOptions(group).map((option) => ({
          group_id: group.id,
          id: option.id,
          name: option.item_name,
          price: Number(option.price || 0),
          duration_min: Number(option.service_duration_min || 0),
        }))
      );

      this.$emit("add", {
        service_id: this.service.id,
        selection: this.selection,
        add_ons: addOns,
        note: this.note.trim(),
        total_price: this.totalPrice,
        total_duration_min: this.totalDurationMin,
      });
    },
  },
};
</script>

<style scoped>
/* Tokens come from src/salon/styles/tokens.css, namespaced under .salon-app. */
.salon-sheet-root {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface);
}

.salon-sheet-scrim {
  position: absolute;
  inset: 0;
  background: rgba(1, 1, 0, 0.4);
}

.salon-sheet {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: var(--salon-max-width);
  height: 90vh;
  margin: 0 auto;
  overflow: hidden;
  background: var(--salon-surface);
  border-radius: 24px 24px 0 0;
  box-shadow: 0 -4px 20px rgba(28, 28, 30, 0.08);
}

.salon-sheet-handle {
  position: absolute;
  top: 12px;
  left: 50%;
  z-index: 20;
  width: 48px;
  height: 6px;
  background: var(--salon-surface-variant);
  border-radius: 999px;
  opacity: 0.6;
  transform: translateX(-50%);
}

/* Pinned over the hero image, the way the reference draws it. */
.salon-sheet-progress {
  position: absolute;
  top: 32px;
  z-index: 20;
  width: 100%;
  padding: 0 var(--salon-container-padding);
  text-align: center;
}

.salon-progress-track {
  width: 100%;
  height: 3px;
  overflow: hidden;
  background: var(--salon-border);
  border-radius: 999px;
}

.salon-progress-fill {
  height: 100%;
  background: var(--salon-primary);
  border-radius: 999px;
  transition: width 0.3s ease;
}

.salon-progress-label {
  display: inline-block;
  margin-top: 8px;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

.salon-sheet-body {
  flex: 1;
  overflow-y: auto;
}

/* ── Hero ──────────────────────────────────────────────────────── */
.salon-sheet-hero {
  position: relative;
  width: 100%;
  height: 180px;
  overflow: hidden;
  background: var(--salon-surface-container-highest);
}

.salon-sheet-hero img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.salon-sheet-close {
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  color: var(--salon-on-surface);
  background: rgba(255, 255, 255, 0.9);
  border: 0;
  border-radius: 999px;
  box-shadow: 0 1px 3px rgba(28, 28, 30, 0.12);
  cursor: pointer;
}

.salon-sheet-close:active {
  transform: scale(0.95);
}

/* ── Detail ────────────────────────────────────────────────────── */
.salon-sheet-content {
  padding: var(--salon-stack-lg) var(--salon-container-padding) 120px;
}

.salon-sheet-title {
  margin: 0 0 var(--salon-stack-sm);
  font-family: var(--salon-font-headline);
  font-size: 28px;
  font-weight: 600;
  line-height: 36px;
  letter-spacing: -0.01em;
  color: var(--salon-on-surface);
}

.salon-sheet-facts {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--salon-stack-lg);
}

.salon-sheet-duration {
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
}

.salon-sheet-price {
  font-family: var(--salon-font-headline);
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: var(--salon-gold);
}

.salon-sheet-desc {
  margin: 0 0 32px;
  font-size: 14px;
  line-height: 22px;
  color: var(--salon-on-surface-variant);
}

/* ── Add-ons ───────────────────────────────────────────────────── */
.salon-addons {
  margin-bottom: 32px;
}

.salon-addons-head {
  display: flex;
  gap: 8px;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: var(--salon-stack-md);
}

.salon-addons-title {
  margin: 0;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-surface);
}

.salon-addons-rule {
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

.salon-addon {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 16px;
  margin-bottom: var(--salon-stack-sm);
  background: var(--salon-surface-lowest);
  border: 1px solid var(--salon-border);
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.salon-addon--on {
  border-color: var(--salon-primary);
}

.salon-addon--off {
  cursor: not-allowed;
  opacity: 0.5;
}

.salon-addon-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.salon-addon-box {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: var(--salon-on-primary);
  background: var(--salon-surface);
  border: 2px solid var(--salon-border);
  border-radius: 3px;
}

.salon-addon-box--round {
  border-radius: 999px;
}

.salon-addon--on .salon-addon-box {
  background: var(--salon-primary);
  border-color: var(--salon-primary);
}

.salon-addon-labels {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.salon-addon-name {
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface);
}

.salon-addon-meta {
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

.salon-addon-price {
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: var(--salon-on-surface-variant);
}

/* ── Notes ─────────────────────────────────────────────────────── */
.salon-notes-input {
  width: 100%;
  padding: 16px;
  margin-top: var(--salon-stack-md);
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface);
  background: var(--salon-surface-lowest);
  border: 1px solid var(--salon-border);
  border-radius: 12px;
  outline: none;
  resize: none;
  transition: border-color 0.2s ease;
}

.salon-notes-input::placeholder {
  color: var(--salon-outline, #77767b);
}

.salon-notes-input:focus {
  border-color: var(--salon-primary);
}

/* ── CTA ───────────────────────────────────────────────────────── */
.salon-sheet-cta {
  position: absolute;
  bottom: 0;
  z-index: 50;
  width: 100%;
  padding: 16px var(--salon-container-padding)
    calc(16px + env(safe-area-inset-bottom, 0px));
  background: var(--salon-surface);
  box-shadow: 0 -4px 20px rgba(28, 28, 30, 0.04);
}

.salon-sheet-hint {
  margin: 0 0 8px;
  font-size: 12px;
  line-height: 16px;
  color: var(--salon-error);
  text-align: center;
}

.salon-cta-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 16px 24px;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-primary);
  background: var(--salon-primary);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.salon-cta-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.salon-cta-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.salon-cta-btn:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.salon-sheet-error {
  position: absolute;
  bottom: 96px;
  width: 100%;
  padding: 0 var(--salon-container-padding);
  margin: 0;
  font-size: 14px;
  color: var(--salon-error);
  text-align: center;
}

.salon-retry {
  display: block;
  padding: 8px 20px;
  margin: 12px auto 0;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  color: var(--salon-primary);
  background: transparent;
  border: 1px solid var(--salon-surface-container-highest);
  border-radius: 999px;
  cursor: pointer;
}
</style>
